import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// Create the context
const DataContext = createContext(null);

// Custom hook to use the data context
export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};

// Helper function to get client type from URL query parameters
const getClientTypeFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const clientType = urlParams.get('client_type');

  // If client_type is 'lavagna', use lavagna folder
  // Otherwise, use default folder
  if (clientType === 'lavagna') {
    return 'lavagna';
  }
  return 'default';
};

// Helper function to get the data folder path based on client type
const getDataFolderPath = (clientType) => {
  return `/${clientType}/`;
};

// Provider component
export const DataProvider = ({ children }) => {
  const [clientType, setClientType] = useState(() => getClientTypeFromUrl());
  const [dataJson, setDataJson] = useState({});
  const [losDataJson, setLosDataJson] = useState({});
  const [graficiDataJson, setGraficiDataJson] = useState({});
  const [scenarioDescriptions, setScenarioDescriptions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Get the folder path based on client type
  const folderPath = useMemo(() => getDataFolderPath(clientType), [clientType]);

  // Fetch all data when client type changes
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      setError(null);
      setDataLoaded(false);

      try {
        console.log(`Loading data from folder: ${folderPath}`);

        const [graphResponse, losResponse, graficiResponse, descriptionsResponse] = await Promise.all([
          axios.get(`${folderPath}grafi_per_scenario.json`),
          axios.get(`${folderPath}los_per_scenario.json`),
          axios.get(`${folderPath}scenario_grafici.json`),
          // Fetch descriptions only if they exist, handle gracefully if not
          axios.get(`${folderPath}scenario_descriptions.json`).catch(() => ({ data: {} }))
        ]);

        setDataJson(graphResponse.data);
        setLosDataJson(losResponse.data);
        setGraficiDataJson(graficiResponse.data);
        setScenarioDescriptions(descriptionsResponse.data);
        setDataLoaded(true);

        console.log(`Data loaded successfully for client type: ${clientType}`);
        console.log('Scenarios available:', graphResponse.data?.scenario?.length || 0);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data');

        // Try to load from default folder as fallback if not already using default
        if (clientType !== 'default') {
          console.log('Attempting to load from default folder as fallback...');
          try {
            const [graphResponse, losResponse, graficiResponse, descriptionsResponse] = await Promise.all([
              axios.get('/default/grafi_per_scenario.json'),
              axios.get('/default/los_per_scenario.json'),
              axios.get('/default/scenario_grafici.json'),
              axios.get('/default/scenario_descriptions.json').catch(() => ({ data: {} }))
            ]);

            setDataJson(graphResponse.data);
            setLosDataJson(losResponse.data);
            setGraficiDataJson(graficiResponse.data);
            setScenarioDescriptions(descriptionsResponse.data);
            setDataLoaded(true);
            setError(null);
            console.log('Fallback to default folder successful');
          } catch (fallbackErr) {
            console.error('Fallback also failed:', fallbackErr);
            setError('Failed to load data from both client folder and default folder');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [clientType, folderPath]);

  // Listen for URL changes (in case client_type changes without page reload)
  useEffect(() => {
    const handleUrlChange = () => {
      const newClientType = getClientTypeFromUrl();
      if (newClientType !== clientType) {
        setClientType(newClientType);
      }
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [clientType]);

  // Helper function to get scenarios list
  const getScenarios = () => {
    return dataJson?.scenario || [];
  };

  // Helper function to get scenario by replicaAvg
  const getScenarioByReplicaAvg = (replicaAvg) => {
    return dataJson?.scenario?.find(s => s.replicaAvg === replicaAvg) || null;
  };

  // Helper function to get LOS data for a scenario
  const getLosDataByReplicaAvg = (replicaAvg) => {
    return losDataJson?.scenario?.find(s => s.replicaAvg === replicaAvg) || null;
  };

  // Helper function to get grafici data for a scenario
  const getGraficiDataByReplicaAvg = (replicaAvg) => {
    return graficiDataJson?.scenario?.find(s => s.replicaAvg === replicaAvg) || null;
  };

  // Helper function to get sistema metrics for a scenario
  const getSistemaMetrics = (scenarioId) => {
    if (!scenarioId || !graficiDataJson.scenario) {
      return { flusso: 0, codaMedia: 0, ritardoMedio: 0 };
    }

    const scenarioData = graficiDataJson.scenario.find(
      scenario => scenario.replicaAvg === scenarioId
    );

    if (scenarioData?.sistema) {
      return {
        flusso: scenarioData.sistema.flowSys || 0,
        codaMedia: scenarioData.sistema.qmeanSys || 0,
        ritardoMedio: scenarioData.sistema.dtimeSys || 0
      };
    }

    return { flusso: 0, codaMedia: 0, ritardoMedio: 0 };
  };

  // Get map center from first scenario
  const getDefaultMapCenter = () => {
    const firstScenario = dataJson?.scenario?.[0];
    if (firstScenario?.center) {
      return [firstScenario.center[1], firstScenario.center[0]];
    }
    return [44.30713712309106, 9.349106110408071]; // Default center
  };

  // Context value
  const value = {
    // Client type
    clientType,
    folderPath,

    // Raw data
    dataJson,
    losDataJson,
    graficiDataJson,
    scenarioDescriptions,

    // Loading state
    isLoading,
    error,
    dataLoaded,

    // Helper functions
    getScenarios,
    getScenarioByReplicaAvg,
    getLosDataByReplicaAvg,
    getGraficiDataByReplicaAvg,
    getSistemaMetrics,
    getDefaultMapCenter,

    // Manual refresh function
    refreshData: () => {
      const newClientType = getClientTypeFromUrl();
      setClientType(newClientType);
    }
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;
